import { Button, Col, Input, Layout, message, Row, Upload, Form, Select } from "antd";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import PlainTable from "../../Components/PlainTable";
import { Equal, TriangleAlert, X, Inbox, CheckCircle, XCircle, ExternalLink, FileText } from "lucide-react";
import { useEffect, useState } from "react";
import {
  formatNumbers,
  formatString,
  handleKeyPress,
  handleOnWheel,
  navigateTo,
} from "../../Utils/Common";
import { useLocation, useParams } from "react-router-dom";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import ConfirmationModal from "../../Components/CustomModals/ConfirmationModal";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

interface EndOfDayFormProps {
  add?: boolean;
  view?: boolean;
  edit?: boolean;
  check?: boolean;
  approval?: boolean;
}

const { Dragger } = Upload;

const initialBillData = [
  { bill: "1,000",   amount: 1000,  key: "peso_1000", count: 0, total: 0 },
  { bill: "500",     amount: 500,   key: "peso_500",  count: 0, total: 0 },
  { bill: "200",     amount: 200,   key: "peso_200",  count: 0, total: 0 },
  { bill: "100",     amount: 100,   key: "peso_100",  count: 0, total: 0 },
  { bill: "50",      amount: 50,    key: "peso_50",   count: 0, total: 0 },
  { bill: "20",      amount: 20,    key: "peso_20",   count: 0, total: 0 },
  { bill: "10 coin", amount: 10,    key: "peso_10",   count: 0, total: 0 },
  { bill: "5 coin",  amount: 5,     key: "peso_5",    count: 0, total: 0 },
  { bill: "1 coin",  amount: 1,     key: "peso_1",    count: 0, total: 0 },
  { bill: "25 cent", amount: 0.25,  key: "cent_25",   count: 0, total: 0 },
  { bill: "10 cent", amount: 0.1,   key: "cent_10",   count: 0, total: 0 },
  { bill: "5 cent",  amount: 0.05,  key: "cent_5",    count: 0, total: 0 },
  { bill: "1 cent",  amount: 0.01,  key: "cent_1",    count: 0, total: 0 },
];

const EndOfDayForm: React.FC<EndOfDayFormProps> = ({ add, view, edit, check, approval }) => {
  // ── Safe action derivation ────────────────────────────────────────────────────
  const action = add ? "add" : view ? "view" : edit ? "edit" : check ? "check" : approval ? "approval" : "add";

  const location = useLocation();
  const { id: eod_id } = useParams<{ id: string }>();

  const [showModal,    setShowModal]    = useState("");
  const [loading,      setLoading]      = useState("");
  const [remarks,      setRemarks]      = useState("");
  const [checkRemarks, setCheckRemarks] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [branchID,     setBranchID]     = useState<string | undefined>(undefined);
  const [branchName,   setBranchName]   = useState("");
  const [attachment,   setAttachment]   = useState<File | null>(null);
  const [branches,     setBranches]     = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showAttachment, setShowAttachment] = useState(false);


  // Cash Sales
  const [cashSalesData,  setCashSalesData]  = useState(initialBillData.map((i) => ({ ...i })));
  const [cashSalesTotal, setCashSalesTotal] = useState(0);

  // Variance
  const [systemCashSales,         setSystemCashSales]         = useState(0);
  const [systemAgentDisbursement, setSystemAgentDisbursement] = useState(0);

  const cashSalesVariance = cashSalesTotal - systemCashSales;

  // Derived helpers
  const isReadOnly  = action === "view" || action === "check" || action === "approval";
  const isEditOrAdd = action === "add"  || action === "edit";
  const isReviewing = action === "check" || action === "approval"; // shows remarks textarea

  // ─── Page title ───────────────────────────────────────────────────────────────
  const pageTitle = (() => {
    switch (action) {
      case "view":     return "View End of Day";
      case "edit":     return "Edit End of Day";
      case "check":    return "Check End of Day";
      case "approval": return "Approve End of Day";
      default:         return "Add End of Day";
    }
  })();

  // ─── Columns ──────────────────────────────────────────────────────────────────
  const getColumns = (
    data: typeof initialBillData,
    handleChange: (e: any, index: number, amount: number) => void
  ) => [
    {
      title: "Bill",
      dataIndex: "bill",
      align: "text-start",
      width: 200,
      render: (text: any) => (
        <span className="text-textColor font-bold">{text}</span>
      ),
    },
    {
      title: "operand",
      dataIndex: "operand",
      width: 50,
      render: () => <X size={15} />,
    },
    {
      title: "Count",
      dataIndex: "count",
      align: "text-start",
      width: 200,
      render: (text: any, record: any, index: number) => (
        <Input
          type="number"
          className="w-full my-1"
          placeholder="0"
          value={text}
          onWheel={(e) => handleOnWheel(e)}
          onKeyDown={(e) => handleKeyPress(e, "-")}
          min={0}
          disabled={isReadOnly}
          onChange={(e) => handleChange(e, index, record.amount)}
        />
      ),
    },
    {
      title: "equal",
      dataIndex: "equal",
      width: 50,
      render: () => <Equal size={15} className="ms-10" />,
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "text-end",
      width: 200,
      render: (text: any) => (
        <span className="text-textColor font-bold">{formatNumbers(text)}</span>
      ),
    },
  ];

  // ─── Upload handlers ──────────────────────────────────────────────────────────
  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  const handleUploadChange = (info: any) => {
    const file = info.fileList?.[0]?.originFileObj ?? null;
    setAttachment(file);
  };

  // ─── Fetch branches ───────────────────────────────────────────────────────────
  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    const response = await getRequest(`branches/get`, true);
    if (response.success) setBranches(response.data.data);
    setLoading("");
  }

  // ─── Cash sales handler ───────────────────────────────────────────────────────
  function handleCashSalesChange(e: any, index: number, amount: number) {
    const count   = e.target.value;
    const updated = [...cashSalesData];
    updated[index] = { ...updated[index], count, total: amount * count };
    setCashSalesData(updated);
    setCashSalesTotal(updated.reduce((acc, item) => acc + item.total, 0));
  }

  // ─── Payload helpers ──────────────────────────────────────────────────────────
  function buildDetailPayload(data: typeof initialBillData) {
    return data.reduce((acc: any, item) => {
      acc[item.key] = item.count ?? 0;
      return acc;
    }, {});
  }

  function mapDetailToData(base: typeof initialBillData, result: any): typeof initialBillData {
    return base.map((item) => {
      const count = parseFloat(result[item.key] ?? 0);
      return { ...item, count, total: item.amount * count };
    });
  }

  // ─── Fetch record ─────────────────────────────────────────────────────────────
  async function fetchData() {
    setLoading("fetch");

    const response = await getRequest(`eods/get`, { eod_id }, true);
    if (!response.success) {
      message.error("Failed to fetch EOD record.");
      setLoading("");
      return;
    }

    const data = response.data.data;
    const eod  = data[0];

    setRemarks(eod.remarks ?? "");
    setBranchID(eod.branch_id ?? undefined);
    setBranchName(eod.branch_name ?? "");
    setSystemCashSales(parseFloat(eod.system_cash_sales ?? 0));
    setSystemAgentDisbursement(parseFloat(eod.system_agent_disbursement ?? 0));
    setAttachmentUrl(eod.attachment ?? "");  

    const cashDetail = data.find((d: any) => d.type === "cash_sale");
    if (cashDetail) {
      const updated = mapDetailToData(initialBillData.map((i) => ({ ...i })), cashDetail);
      setCashSalesData(updated);
      setCashSalesTotal(updated.reduce((acc, item) => acc + item.total, 0));
    }

    if (action === "edit") {
      const branchResponse = await getRequest(`branches/get`, true);
      if (branchResponse.success) setBranches(branchResponse.data.data);
    }

    setLoading("");
  }

  // ─── Save (add) ───────────────────────────────────────────────────────────────
  async function saveCashCount() {
    if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
    setLoading("submit");

    const formData = new FormData();

    formData.append("remarks",            remarks);
    formData.append("actual_cash_sales",  String(cashSalesTotal));
    formData.append("cash_sales_variance",String(cashSalesVariance)); 
    formData.append("cash_sale_detail",   JSON.stringify(buildDetailPayload(cashSalesData)));
    formData.append("branch_id",          branchID ?? "");

    if (attachment) {
      formData.append("attachment", attachment);
    }

    const response = await postRequest(`eods/create`, formData, true);
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => { setShowModal(""); setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      setErrorMessage(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to save End of Day.");
    }
  }

  // ─── Update (edit) ────────────────────────────────────────────────────────────
  async function updateCashCount() {
    if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
    setLoading("submit");

    const formData = new FormData();

    formData.append("eod_id",             String(eod_id));
    formData.append("remarks",            remarks);
    formData.append("actual_cash_sales",  String(cashSalesTotal));
    formData.append("cash_sales_variance",String(cashSalesVariance)); 
    formData.append("cash_sale_detail",   JSON.stringify(buildDetailPayload(cashSalesData)));
    formData.append("branch_id",          branchID ?? "");

    if (attachment) {
      formData.append("attachment", attachment);
    }

    const response = await postRequest(`eods/update`, formData, true);
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => { setShowModal(""); setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      setErrorMessage(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to update End of Day.");
    }
  }

  // ─── Check (marks as "for_approval") ─────────────────────────────────────────
  async function checkEOD() {
    setLoading("check");
    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "for_approval", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => { setShowModal(""); setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      setErrorMessage(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to check End of Day.");
    }
  }

  // ─── Approve (marks as "approved") ───────────────────────────────────────────
  async function approveEOD() {
    setLoading("approve");
    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "approved", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => { setShowModal(""); setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      setErrorMessage(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to approve End of Day.");
    }
  }

  // ─── Reject (works for both check and approval) ───────────────────────────────
  async function rejectEOD() {
    if (!checkRemarks.trim()) {
      message.warning("Please provide remarks before rejecting.");
      return;
    }
    setLoading("reject");

    const response = await postRequest(
      `eods/change_status`,
      { eod_id, new_status: "disapproved", check_remarks: checkRemarks },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => { setShowModal(""); setLoading(""); navigateTo("/end-of-day"); }, 1000);
    } else {
      setLoading(""); setShowModal("");
      setErrorMessage(response?.data?.response?.data?.messages?.error_messages?.[0] ?? "Failed to reject End of Day.");
    }
  }

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (action === "add") {
      fetchBranches();
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Variance row ─────────────────────────────────────────────────────────────
  const VarianceRow = ({ label, actual, system, variance }: {
    label: string; actual: number; system: number; variance: number;
  }) => (
    <div className="grid grid-cols-4 gap-4 py-2 border-b border-gray-200 text-sm">
      <span className="font-semibold col-span-1">{label}</span>
      <span className="text-end">{formatNumbers(actual)}</span>
      <span className="text-end">{formatNumbers(system)}</span>
      <span className={`text-end font-bold ${variance < 0 ? "text-red-500" : variance > 0 ? "text-green-600" : "text-textColor"}`}>
        {formatNumbers(variance)}
      </span>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          {loading !== "fetch" && (
            <Col span={24} className="text-textColor">
              <div className="form-container">

                {/* ── Branch ───────────────────────────────── */}
                <div className="mt-5 pt-3 pb-2 flex flex-col gap-2">
                  <span className="font-bold">Branch</span>
                  {isReadOnly ? (
                    <span className="text-textColor font-medium">{branchName || "—"}</span>
                  ) : (
                    <Select
                      placeholder="Select branch"
                      showSearch
                      allowClear
                      value={branchID}
                      onChange={(value) => setBranchID(value)}
                      options={branches.map((b) => ({ value: b.id, label: b.name }))}
                      filterOption={(input, option) =>
                        option?.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  )}
                </div>

                {/* ── Cash Sales ───────────────────────────── */}
                <Row className="w-full mb-2 mt-4">
                  <Col><h2><strong>Total Cash Sales Collected</strong></h2></Col>
                </Row>
                <PlainTable
                  headers={getColumns(cashSalesData, handleCashSalesChange)}
                  rows={cashSalesData}
                  withHeader={false}
                  withBorder={false}
                />
                <div className="border-t border-t-textColor mt-5 pt-3 pb-2 flex justify-between">
                  <span className="font-bold text-xl">TOTAL (Cash Sales)</span>
                  <span className="font-bold text-xl">{formatNumbers(cashSalesTotal)}</span>
                </div>

                {/* ── Variance Summary ─────────────────────── */}
                <div className="mt-8">
                  <h2 className="mb-3"><strong>Variance Summary</strong></h2>
                  <div className="grid grid-cols-4 gap-4 py-2 border-b border-gray-400 text-sm font-bold">
                    <span>Type</span>
                    <span className="text-end">Actual</span>
                    <span className="text-end">System</span>
                    <span className="text-end">Variance</span>
                  </div>
                  <VarianceRow label="Cash Sales" actual={cashSalesTotal} system={systemCashSales} variance={cashSalesVariance} />
                </div>

                {/* ── Remarks ──────────────────────────────── */}
                <div className="mt-5 pt-3 pb-2 flex flex-col gap-2">
                  <span className="font-bold">REMARKS</span>
                  <Input.TextArea value={remarks} disabled={isReadOnly} rows={3} onChange={(e) => setRemarks(e.target.value)} />
                </div>

                {/* ── Reviewer Remarks (check + approval) ──── */}
                {/* {isReviewing && (
                  <div className="mt-5 pt-3 pb-2 flex flex-col gap-2">
                    <span className="font-bold">
                      {action === "approval" ? "APPROVAL" : "CHECK"} REMARKS
                      <span className="text-red-500 ms-1">*</span>
                      <span className="text-gray-400 text-xs ms-2 font-normal">(required when rejecting)</span>
                    </span>
                    <Input.TextArea
                      value={checkRemarks}
                      rows={3}
                      placeholder={`Enter ${action === "approval" ? "approver" : "checker"} remarks...`}
                      onChange={(e) => setCheckRemarks(e.target.value)}
                    />
                  </div>
                )} */}

                {/* ── Attachment ───────────────────────────── */}
                {isEditOrAdd && (
                  <Col xs={24} className="pt-1">
                    <span className="edit-label mb-2">
                      {/* <label className="text-red-500 me-1">*</label> */}
                      Deposit Slip:
                    </span>
                    <Form.Item
                      name="attachment"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => e?.fileList || []}
                      rules={[{ required: action === "add", message: "Deposit slip is required." }]}
                    >
                      <Dragger name="file" beforeUpload={handleBeforeUpload} onChange={handleUploadChange} accept=".pdf,image/*">
                        <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                        <p className="ant-upload-text">Click or drag file to upload</p>
                        <p className="ant-upload-hint">Supports PDF, Image formats.</p>
                      </Dragger>
                    </Form.Item>
                    <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                  </Col>
                )}

                {isReadOnly && attachmentUrl && (
                  <Col xs={24} className="pt-1 mt-4">
                    <span className="font-bold block mb-2">EOD Attachment</span>
                    <Button
                      icon={<ExternalLink size={14} />}
                      onClick={() => setShowAttachment(true)}
                    >
                      View Attachment
                    </Button>
                  </Col>
                )}

                {/* ── Actions ──────────────────────────────── */}
                <Col span={24}>
                  <Row className="flex justify-end px-10 my-5 pb-[50px] sm:pb-0" gutter={[10, 10]}>

                    <Col xs={24} md={12} lg={3} onClick={() => navigateTo("/end-of-day")}>
                      <Button className="w-full">{isReadOnly ? "Close" : "Cancel"}</Button>
                    </Col>

                    {action === "add" && (
                      <Col xs={24} md={12} lg={3}>
                        <Button 
                          className="w-full" type="primary" onClick={() => {
                            if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
                            setShowModal("confirm");
                          }}
                          disabled={!branchID}
                        >
                          Save
                        </Button>
                      </Col>
                    )}

                    {action === "edit" && (
                      <Col xs={24} md={12} lg={3}>
                        <Button 
                          className="w-full" type="primary" onClick={() => {
                            if (cashSalesTotal === 0) { message.warning("Total cannot be zero."); return; }
                            setShowModal("confirm-edit");
                          }}
                          disabled={!branchID}
                        >
                            Update
                          </Button>
                      </Col>
                    )}

                    {/* check — Reject + Check */}
                    {action === "check" && (
                      <>
                        {/* <Col xs={24} md={12} lg={3}>
                          <Button className="w-full" danger
                            onClick={() => setShowModal("confirm-reject")}>
                            Reject
                          </Button>
                        </Col> */}
                        <Col xs={24} md={12} lg={3}>
                          <Button 
                            className="w-full" type="primary"
                            onClick={() => setShowModal("confirm-check")}
                            disabled={!branchID}
                          >
                            Check
                          </Button>
                        </Col>
                      </>
                    )}

                    {/* approval — Reject + Approve */}
                    {action === "approval" && (
                      <>
                        <Col xs={24} md={12} lg={3}>
                          <Button 
                            className="w-full" danger
                            onClick={() => setShowModal("confirm-reject")}
                            disabled={!branchID}
                          >
                            Reject
                          </Button>
                        </Col>
                        <Col xs={24} md={12} lg={3}>
                          <Button 
                            className="w-full" type="primary"
                            onClick={() => setShowModal("confirm-approve")}
                            disabled={!branchID}
                          >
                            Approve
                          </Button>
                        </Col>
                      </>
                    )}

                  </Row>
                </Col>
              </div>
            </Col>
          )}
        </Row>
      </Layout>

      {/* Save */}
      <ConfirmationModal
        title="Are you sure you want to save this End of Day record?"
        footer={<div className="flex justify-center gap-3 mt-5"><Button onClick={() => setShowModal("")}>Cancel</Button><Button type="primary" loading={loading === "submit"} onClick={saveCashCount}>Confirm</Button></div>}
        icon={<TriangleAlert className="text-orange-400 mt-5" size={60} />}
        showModal={showModal === "confirm"} onCancel={() => setShowModal("")}
      />

      {/* Update */}
      <ConfirmationModal
        title="Are you sure you want to update this End of Day record?"
        footer={<div className="flex justify-center gap-3 mt-5"><Button onClick={() => setShowModal("")}>Cancel</Button><Button type="primary" loading={loading === "submit"} onClick={updateCashCount}>Confirm</Button></div>}
        icon={<TriangleAlert className="text-orange-400 mt-5" size={60} />}
        showModal={showModal === "confirm-edit"} onCancel={() => setShowModal("")}
      />

      {/* Check */}
      <ConfirmationModal
        title="Are you sure you want to mark this record as for_approval?"
        footer={<div className="flex justify-center gap-3 mt-5"><Button onClick={() => setShowModal("")}>Cancel</Button><Button type="primary" loading={loading === "check"} onClick={checkEOD}>Confirm</Button></div>}
        icon={<CheckCircle className="text-green-500 mt-5" size={60} />}
        showModal={showModal === "confirm-check"} onCancel={() => setShowModal("")}
      />

      {/* Approve */}
      <ConfirmationModal
        title="Are you sure you want to approve this End of Day record?"
        footer={<div className="flex justify-center gap-3 mt-5"><Button onClick={() => setShowModal("")}>Cancel</Button><Button type="primary" loading={loading === "approve"} onClick={approveEOD}>Approve</Button></div>}
        icon={<CheckCircle className="text-green-500 mt-5" size={60} />}
        showModal={showModal === "confirm-approve"} onCancel={() => setShowModal("")}
      />

      {/* Reject (shared — revert target differs per action, handled inside rejectEOD) */}
      <ConfirmationModal
        title="Are you sure you want to reject this End of Day record?"
        content={
          <div className="mt-3 pb-2 flex flex-col gap-2">
            <span className="font-bold">
              {action === "approval" ? "APPROVAL" : "CHECK"} REMARKS
              <span className="text-red-500 ms-1">*</span>
              <span className="text-gray-400 text-xs ms-2 font-normal">(required when rejecting)</span>
            </span>
            <Input.TextArea
              value={checkRemarks}
              rows={3}
              placeholder={`Enter ${action === "approval" ? "approver" : "checker"} remarks...`}
              onChange={(e) => setCheckRemarks(e.target.value)}
            />
          </div>
        }
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => setShowModal("")}>Cancel</Button>
            <Button danger loading={loading === "reject"} onClick={rejectEOD}>Reject</Button>
          </div>
        }
        icon={<XCircle className="text-red-500 mt-5" size={60} />}
        showModal={showModal === "confirm-reject"}
        onCancel={() => setShowModal("")}
      />

      {/* Error */}
      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <ViewAttachmentModal
        showModal={showAttachment}
        attachmentUrl={attachmentUrl}
        onCancel={() => setShowAttachment(false)}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default EndOfDayForm;